/**
 * Splitting work into request-sized pieces and running them with a little
 * concurrency, so a 1,700-row import is a few dozen requests rather than one
 * huge one or 1,700 small ones.
 */

/** Server caps per request (the backend rejects anything larger). */
export const CHECK_CHUNK = 2000;
export const MESSAGED_CHUNK = 500;
export const LOG_CHUNK = 25;
/** Upload bytes per log request, so 25 large screenshots don't make one 100 MB request. */
export const LOG_CHUNK_BYTES = 20 * 1024 * 1024;
export const LOG_CONCURRENCY = 2;

export function chunk<T>(items: ReadonlyArray<T>, size: number): T[][] {
  if (size < 1) throw new Error('chunk size must be at least 1');
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Chunks of at most `maxItems` whose summed `weight` stays under `maxWeight`.
 * A single item heavier than the cap still gets its own chunk.
 */
export function chunkByWeight<T>(
  items: ReadonlyArray<T>,
  maxItems: number,
  maxWeight: number,
  weight: (item: T) => number,
): T[][] {
  const out: T[][] = [];
  let cur: T[] = [];
  let curWeight = 0;
  for (const item of items) {
    const w = Math.max(0, weight(item));
    if (cur.length > 0 && (cur.length >= maxItems || curWeight + w > maxWeight)) {
      out.push(cur);
      cur = [];
      curWeight = 0;
    }
    cur.push(item);
    curWeight += w;
  }
  if (cur.length > 0) out.push(cur);
  return out;
}

/**
 * Run `worker` over every task with at most `concurrency` in flight. Stops
 * starting new tasks once `shouldStop()` turns true (already-running tasks
 * finish). Resolves when everything started has settled; the worker owns
 * its own error handling.
 */
export async function runPool<T>(
  tasks: ReadonlyArray<T>,
  concurrency: number,
  worker: (task: T, index: number) => Promise<void>,
  shouldStop: () => boolean = () => false,
): Promise<void> {
  let next = 0;
  const lanes = Array.from({ length: Math.max(1, Math.min(concurrency, tasks.length)) }, async () => {
    while (next < tasks.length && !shouldStop()) {
      const i = next++;
      await worker(tasks[i]!, i);
    }
  });
  await Promise.all(lanes);
}
